import { createFeatureSelector, createSelector, MemoizedSelector } from '@ngrx/store';
import { selectUserStoreSettled, selectUserStoreValue } from '../../user-store/selectors/user-store.selector';
import { userStoreValueToConfigState, userStoreValueToTrackingState } from '../location-tracker.converters';
import {
    LOCATION_TRACKER_FEATURE_KEY,
    LOCATION_TRACKER_STATE_STORE_KEY,
    LOCATION_TRACKER_STORE_KEY,
    LocationTrackerCompanionPushGate,
    LocationTrackerCompanionStartOfWorkPushGate,
    LocationTrackerErrorKey,
    LocationTrackerSaveMessage,
    LocationTrackerSaveMessageKey,
    LocationTrackerSaveStatus,
    LocationTrackerState,
    LocationTrackerUserConfigState,
    createDefaultLocationTrackerUserConfigState,
} from '../location-tracker.state';

export const selectLocationTrackerFeature = createFeatureSelector<LocationTrackerState>(LOCATION_TRACKER_FEATURE_KEY);

/** The raw draft — `undefined` until the seed effect has populated it. */
export const selectLocationTrackerDraft: MemoizedSelector<object, LocationTrackerUserConfigState | undefined> =
    createSelector(selectLocationTrackerFeature, (state) => state.draft);

/**
 * Whether the draft has been seeded. This is what the page gates its form on, replacing the old
 * `selectLocationTrackerSettled` (backend-load) gate — see `LocationTrackerEffects.seedLocationTrackerDraft$`
 * for why seeding itself waits for the user store to settle before ever populating this.
 */
export const selectIsLocationTrackerDraftReady: MemoizedSelector<object, boolean> = createSelector(
    selectLocationTrackerDraft,
    (draft) => draft !== undefined,
);

/** The (possibly still-being-edited) user config, defaulting to the empty config before it is seeded. */
export const selectLocationTrackerUserConfig: MemoizedSelector<object, LocationTrackerUserConfigState> = createSelector(
    selectLocationTrackerDraft,
    (draft) => draft ?? createDefaultLocationTrackerUserConfigState(),
);

/**
 * The `LocationTracker` key's stored value, converted to state. Built once at module level — not
 * per call — so every caller shares the same memoized selector instance.
 */
const selectStoredLocationTrackerConfigValue = selectUserStoreValue(LOCATION_TRACKER_STORE_KEY);
export const selectStoredLocationTrackerConfig: MemoizedSelector<object, LocationTrackerUserConfigState> =
    createSelector(selectStoredLocationTrackerConfigValue, userStoreValueToConfigState);

/** The `LocationTrackerState` key's stored value, converted to state — see `selectStoredLocationTrackerConfig`. */
const selectStoredLocationTrackerTrackingStateValue = selectUserStoreValue(LOCATION_TRACKER_STATE_STORE_KEY);
export const selectStoredLocationTrackerTrackingState = createSelector(
    selectStoredLocationTrackerTrackingStateValue,
    userStoreValueToTrackingState,
);

/** Replaces `LocationTrackerModel.isPrivacyPerimeterSet`: both coordinates must be set. */
export const selectIsPrivacyPerimeterSet: MemoizedSelector<object, boolean> = createSelector(
    selectLocationTrackerUserConfig,
    (config) => config.privacyPerimeterLatitude !== undefined && config.privacyPerimeterLongitude !== undefined,
);

/** Whether a geolocation lookup is currently in flight. */
export const selectIsPrivacyPerimeterRequestPending: MemoizedSelector<object, boolean> = createSelector(
    selectLocationTrackerFeature,
    (state) => state.isPerimeterRequestPending,
);

/** The translation key for the last failed geolocation lookup, or `undefined` if none/cleared. */
export const selectPrivacyPerimeterErrorKey: MemoizedSelector<object, LocationTrackerErrorKey | undefined> =
    createSelector(selectLocationTrackerFeature, (state) => state.perimeterErrorKey);

/** Whether a save is currently in flight. */
export const selectIsLocationTrackerSaving: MemoizedSelector<object, boolean> = createSelector(
    selectLocationTrackerFeature,
    (state) => state.saveStatus === LocationTrackerSaveStatus.Saving,
);

/**
 * The save banner to show, derived purely from `saveStatus`: `Saved` -> success message,
 * `Failed` -> error message, anything else -> no banner.
 */
export const selectLocationTrackerSaveMessage: MemoizedSelector<object, LocationTrackerSaveMessage | undefined> =
    createSelector(selectLocationTrackerFeature, (state): LocationTrackerSaveMessage | undefined => {
        switch (state.saveStatus) {
            case LocationTrackerSaveStatus.Saved:
                return { isError: false, key: LocationTrackerSaveMessageKey.Success };
            case LocationTrackerSaveStatus.Failed:
                return { isError: true, key: LocationTrackerSaveMessageKey.Error };
            default:
                return undefined;
        }
    });

/**
 * What both companion pushes are gated on, in one selector, so
 * `LocationTrackerCompanionPushEffects` can wait on a single `isReady` flag instead of coordinating
 * the user store's own async load itself.
 */
export const selectCompanionPushGate: MemoizedSelector<object, LocationTrackerCompanionPushGate> = createSelector(
    selectUserStoreSettled,
    selectStoredLocationTrackerConfig,
    (isReady, config) => ({ isReady, shareLocation: config.shareLocation }),
);

/** {@link selectCompanionPushGate} plus the start-of-work push's own dedupe marker. */
export const selectCompanionStartOfWorkPushGate: MemoizedSelector<object, LocationTrackerCompanionStartOfWorkPushGate> =
    createSelector(selectCompanionPushGate, selectStoredLocationTrackerTrackingState, (gate, trackingState) => ({
        ...gate,
        storedStartOfWork: trackingState.startOfWork,
    }));
